/**
 * The hydration runtime body (the only JS that runs in the iframe), kept as a
 * plain string for injection. Split from hydrationRuntime.ts for the file-size
 * convention. See that file for the security model.
 */

export const RUNTIME_BODY = `
(function () {
  "use strict";
  function setText(el, text) {
    // textContent only — never innerHTML — so values can't inject markup.
    el.textContent = text;
  }
  // Latest hydrated value per binding key — charts read this on (re)render.
  var latestBindings = {};
  function applyBinding(key, payload) {
    latestBindings[key] = payload || null;
    var nodes = document.querySelectorAll('[data-binding="' + CSS.escape(key) + '"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // Chart containers are hydrated by renderCharts(), not as text cells.
      if (el.hasAttribute("data-chart")) continue;
      // Clear any prior-refresh state markers so stale flags don't linger.
      el.removeAttribute("data-binding-error");
      el.removeAttribute("data-binding-stale");
      el.removeAttribute("data-binding-structured");
      if (el.title === "Couldn't refresh — showing last known value") el.removeAttribute("title");
      if (!payload) continue;
      if (payload.error) {
        // Keep the last-known value visible but flag it as stale so the cell
        // (and the parent badge) can show a "couldn't refresh" state.
        el.setAttribute("data-binding-error", String(payload.error));
        el.title = "Couldn't refresh — showing last known value";
        var lastKnown = payload.value;
        if (lastKnown !== null && lastKnown !== undefined && typeof lastKnown !== "object") {
          setText(el, String(lastKnown));
        }
        el.setAttribute("data-binding-stale", "1");
        continue;
      }
      var value = payload.value;
      if (value === null || value === undefined) {
        // A genuine empty result (e.g. a KPI over zero rows). Render an em-dash
        // so it's clearly "no data", not a real zero and not un-hydrated.
        setText(el, "\u2014");
        el.setAttribute("data-binding-hydrated", "1");
        continue;
      }
      if (Array.isArray(value) && renderRows(el, value)) {
        el.setAttribute("data-binding-hydrated", "1");
        continue;
      }
      if (typeof value === "object") {
        // Structured payload (series/pivot) with no scalar to show.
        // Charts will consume these later; for now surface a neutral marker
        // instead of leaving the misleading publish-time placeholder.
        var count = Array.isArray(value)
          ? value.length
          : (value && Array.isArray(value.x) ? value.x.length : null);
        setText(el, count !== null ? "\u2014" : "\u2014");
        el.setAttribute("data-binding-structured", "1");
        el.setAttribute("data-binding-hydrated", "1");
        continue;
      }
      setText(el, String(value));
      el.setAttribute("data-binding-hydrated", "1");
    }
  }

  // ---- Tables ------------------------------------------------------------
  // A cell bound to a list of rows (e.g. "top 5 open deals") renders as a small table:
  // one column per field, a row's url as a link on its first column. DOM API only.
  var MAX_ROWS = 50;
  function columnLabel(key) {
    var text = String(key).replace(/_/g, " ");
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  function cellText(v) {
    if (v === null || v === undefined || v === "") return "\u2014";
    if (typeof v === "number") return Number.isFinite(v) ? v.toLocaleString() : String(v);
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  }
  function linkCell(td, text, url) {
    // The iframe can't open windows itself: ask the viewer page to (it accepts https links only).
    var a = document.createElement("a");
    a.textContent = text;
    a.href = url;
    a.rel = "noopener noreferrer";
    a.addEventListener("click", function (ev) {
      ev.preventDefault();
      window.parent.postMessage({ type: "micro-app:open-link", url: url }, "*");
    });
    td.appendChild(a);
  }
  function renderRows(el, rows) {
    var first = rows[0];
    if (!first || typeof first !== "object" || Array.isArray(first)) return false;
    var keys = Object.keys(first).filter(function (k) { return k !== "url"; });
    if (!keys.length) return false;
    var table = document.createElement("table");
    table.setAttribute("data-binding-table", "1");
    table.style.width = "100%";
    table.style.borderCollapse = "collapse";
    var head = table.createTHead().insertRow();
    keys.forEach(function (k) {
      var th = document.createElement("th");
      th.textContent = columnLabel(k);
      th.style.textAlign = "left";
      th.style.padding = "4px 8px";
      head.appendChild(th);
    });
    var body = table.createTBody();
    rows.slice(0, MAX_ROWS).forEach(function (row) {
      var tr = body.insertRow();
      keys.forEach(function (k, i) {
        var td = tr.insertCell();
        var v = row ? row[k] : null;
        td.style.padding = "4px 8px";
        if (typeof v === "number") td.style.textAlign = "right";
        var url = row && typeof row.url === "string" && row.url.slice(0, 8).toLowerCase() === "https://" ? row.url : null;
        if (i === 0 && url) linkCell(td, cellText(v), url);
        else td.textContent = cellText(v);
      });
    });
    el.textContent = "";
    el.appendChild(table);
    return true;
  }

  // ---- Charts ------------------------------------------------------------
  // The LLM emits inert declarative chart specs (data-chart + data-binding +
  // data-x/y/title). Our trusted, injected frappe-charts runtime reads the spec
  // + the hydrated series/pivot data and renders a real SVG chart. The library
  // is only present when the document actually contains a chart (see
  // injectHydrationRuntime), so guard on it.
  var CHART_TYPES = { bar: "bar", line: "line", area: "line", pie: "pie", donut: "percentage", percentage: "percentage" };
  var chartInstances = {};

  function brandColors() {
    // Pull brand palette from the doc so charts match the dashboard. Falls back
    // to a neutral set the runtime ships with.
    var cs = getComputedStyle(document.documentElement);
    var vars = ["--brand-primary", "--brand-secondary", "--brand-accent-1", "--brand-accent-2", "--brand-accent-3"];
    var out = [];
    for (var i = 0; i < vars.length; i++) {
      var v = cs.getPropertyValue(vars[i]).trim();
      if (v) out.push(v);
    }
    return out.length ? out : ["#5e64ff", "#743ee2", "#ff5858", "#ffa00a", "#28a745"];
  }

  function toChartData(value, spec) {
    // series transform -> {x:[], y:[]}; pivot transform -> {label: value}.
    // Guard against non-numeric values: Number() coerces garbage to NaN, which
    // frappe draws as a broken plot instead of falling back cleanly.
    if (value && Array.isArray(value.x) && Array.isArray(value.y)) {
      var ys = value.y.map(Number);
      if (!ys.every(Number.isFinite)) return null;
      return { labels: value.x.map(String), datasets: [{ name: spec.yName || "", values: ys }] };
    }
    if (Array.isArray(value)) return rowsToChartData(value, spec);
    if (value && typeof value === "object" && !Array.isArray(value)) {
      var labels = [], values = [];
      for (var k in value) {
        if (Object.prototype.hasOwnProperty.call(value, k)) { labels.push(String(k)); values.push(Number(value[k])); }
      }
      if (!values.every(Number.isFinite)) return null;
      if (labels.length) return { labels: labels, datasets: [{ name: spec.yName || "", values: values }] };
    }
    return null;
  }

  // A list of rows (e.g. a top_n transform) -> the first text column as labels and the first
  // number column as values, so a chart bound to rows still draws instead of staying a fallback.
  function rowsToChartData(rows, spec) {
    var first = rows.length && rows[0] && typeof rows[0] === "object" ? rows[0] : null;
    if (!first) return null;
    var labelKey = null, valueKey = null;
    for (var k in first) {
      if (!Object.prototype.hasOwnProperty.call(first, k)) continue;
      var v = first[k];
      if (labelKey === null && typeof v === "string" && !Number.isFinite(Number(v))) labelKey = k;
      else if (valueKey === null && v !== null && v !== "" && Number.isFinite(Number(v))) valueKey = k;
    }
    if (labelKey === null || valueKey === null) return null;
    var values = rows.map(function (r) { return Number(r && r[valueKey]); });
    if (!values.every(Number.isFinite)) return null;
    var labels = rows.map(function (r) { return String(r && r[labelKey]); });
    return { labels: labels, datasets: [{ name: spec.yName || valueKey, values: values }] };
  }

  function renderOneChart(el) {
    if (!window.frappe || !window.frappe.Chart) return;
    var rawType = (el.getAttribute("data-chart") || "").toLowerCase();
    var type = CHART_TYPES[rawType];
    var key = el.getAttribute("data-binding");
    if (!type || !key) return; // unknown chart type / no binding -> keep fallback
    var payload = latestBindings[key];
    if (!payload || payload.error) {
      // No data or an error. If the chart already rendered once, keep that good
      // chart visible (a transient refresh error must never blank it). If it has
      // NEVER rendered (e.g. a preview whose bindings failed to load on first
      // paint), collapse the empty container so it doesn't sit as a blank
      // bordered plot next to populated KPIs.
      if (payload && payload.error) el.setAttribute("data-chart-stale", "1");
      if (!chartInstances[key]) {
        el.style.display = "none";
        el.setAttribute("data-chart-collapsed", "1");
      }
      return;
    }
    // Have data: un-collapse if a prior failed load hid the empty container.
    if (el.getAttribute("data-chart-collapsed")) {
      el.style.display = "";
      el.removeAttribute("data-chart-collapsed");
    }
    var spec = {
      title: el.getAttribute("data-title") || "",
      yName: el.getAttribute("data-y") || "",
    };
    var data = toChartData(payload.value, spec);
    if (!data) return; // structured data we can't chart -> keep fallback

    el.removeAttribute("data-chart-stale");
    // Recreate on each refresh: frappe has no uniform in-place update across
    // types, and recreation is cheap for dashboard-sized data.
    if (chartInstances[key] && chartInstances[key].destroy) {
      try { chartInstances[key].destroy(); } catch (e) {}
    }
    el.textContent = ""; // clear the pre-hydration fallback before mounting
    try {
      var cfg = {
        data: data,
        type: type,
        height: Number(el.getAttribute("data-height")) || 240,
        colors: brandColors(),
        animate: true,
        axisOptions: { xIsSeries: type === "line" },
      };
      // "area" is a line chart with the region filled — distinguishes it visually.
      if (rawType === "area") cfg.lineOptions = { regionFill: 1, hideDots: 0 };
      chartInstances[key] = new window.frappe.Chart(el, cfg);
      el.setAttribute("data-chart-rendered", "1");
    } catch (e) {
      // Rendering failed: restore a minimal textual fallback so it's not blank.
      el.setAttribute("data-chart-error", "1");
    }
  }

  function renderCharts() {
    var nodes = document.querySelectorAll("[data-chart][data-binding]");
    for (var i = 0; i < nodes.length; i++) {
      try { renderOneChart(nodes[i]); } catch (e) { /* isolate per-chart */ }
    }
    // Nudge frappe to re-measure the container after layout settles. On first
    // mount inside our nested flex/iframe layout the SVG can be measured before
    // final width is known, which scales the viewBox and throws off the tooltip
    // coordinate math. Frappe recomputes width + redraws on window "resize", so
    // firing one realigns the charts and their tooltips.
    if (nodes.length) {
      setTimeout(function () {
        try { window.dispatchEvent(new Event("resize")); } catch (e) {}
      }, 60);
    }
  }

  var hydrated = false;
  window.addEventListener("message", function (event) {
    var data = event.data;
    if (!data || data.type !== "micro-app:hydrate" || !data.bindings) return;
    hydrated = true;
    var bindings = data.bindings;
    for (var key in bindings) {
      if (Object.prototype.hasOwnProperty.call(bindings, key)) {
        try { applyBinding(key, bindings[key]); } catch (e) { /* isolate per-binding */ }
      }
    }
    try { renderCharts(); } catch (e) { /* charts never block hydration */ }
    // Tell the parent hydration finished (for the "last updated" badge timing).
    if (window.parent) window.parent.postMessage({ type: "micro-app:hydrated" }, "*");
  });
  // Announce readiness so the parent knows when to post data. Because the parent's
  // message listener may not be attached yet when this first fires, keep
  // re-announcing until we actually receive data (or give up after ~5s). This
  // closes the race where the very first "ready" is missed.
  function announce() {
    if (hydrated || !window.parent) return;
    window.parent.postMessage({ type: "micro-app:ready" }, "*");
  }
  announce();
  var attempts = 0;
  var readyTimer = setInterval(function () {
    attempts++;
    if (hydrated || attempts > 25) { clearInterval(readyTimer); return; }
    announce();
  }, 200);
})();
`;
